import { FormatType } from '@revelio/replots';

import type { MetricValues } from '../../PageConfigProvider/types/metric';

export const getFormat = ({
  metric,
  plotFormat,
  displayAs,
}: {
  metric?: MetricValues;
  plotFormat?: FormatType;
  displayAs?: 'value' | 'share';
}) => {
  if (plotFormat !== undefined) {
    return plotFormat;
  }

  if (
    metric === 'attrition_rate' ||
    metric === 'growth_rate' ||
    metric === 'hiring_rate'
  ) {
    return FormatType.PERCENTAGE;
  }

  switch (metric) {
    case 'share_of_headcount':
    case 'overall':
    // The diverging sentiment metric: its value is (score - 0.5), which the
    // product renders as a signed percent — DivergingBar hardcoded that
    // because this case was missing here. Keeping them in sync means the
    // narration digest produces the same string the chart tooltip shows.
    case 'sentiment_top_bottom':
    case 'business_outlook':
    case 'career_growth':
    case 'compensation':
    case 'benefits':
    case 'culture':
    case 'healthcare':
    case 'retirement_pension':
    case 'childcare':
    case 'education_assistance':
    case 'wellness_programs':
    case 'work_life_balance':
    case 'diversity_and_inclusion':
    case 'leadership':
    case 'remote_work':
    case 'onboarding':
    case 'mentorship':
    case 'technology':
    case 'promotion_opportunities':
    case 'paid_time_off':
    case 'transparency':
    case 'efficiency_and_bureaucracy':
    case 'product_and_service_quality':
    case 'prestige':
    case 'coworkers':
    case 'mission_and_vision':
    case 'workplace_facilities':
    case 'training':
    case 'clients_and_customers':
    case 'company_growth':
    case 'schedule_flexibility':
    case 'job_security':
    case 'overtime':
    case 'family_support':
    case 'commission':
    case 'independence':
    case 'commute':
    case 'safety':
    case 'internal_communication':
    case 'office_politics':
    case 'harassment':
    case 'honesty':
    case 'ethics':
    case 'commitment':
    case 'compliance':
    case 'integrity':
    case 'screener_growth':
    case 'screener_growth_yoy':
    case 'screener_hiring':
    case 'screener_attrition':
      return FormatType.PERCENTAGE;

    case 'time_to_fill':
      return FormatType.DAY;

    case 'tenure':
    case 'screener_tenure':
      return FormatType.YEAR;

    case 'salary':
    case 'screener_salary':
    case 'posting_salary':
    case 'total_compensation':
    case 'base_salary':
    case 'contractor_pay_rate':
    case 'total_spend_salary':
    case 'total_spend_compensation':
      return FormatType.CURRENCY;

    case 'headcount':
      return displayAs === 'share' ? FormatType.PERCENTAGE : FormatType.INTEGER;

    case 'screener_headcount':
    case 'subsidiary_count':
      return FormatType.INTEGER;

    case 'demand_supply':
      return FormatType.RATIO;

    case 'demand_supply_percentile':
    case 'wage_pressure':
    case 'wage_pressure_percentile':
    case 'time_to_fill_percentile':
    case 'market_tightness':
    case 'market_share':
      return FormatType.PERCENTAGE;

    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'transitions_inflow':
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'transitions_outflow':
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'postings_new':
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'postings_removed':
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'postings_volume':
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'postings_active':
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'screener_inflow':
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case 'screener_outflow':
    default:
      return FormatType.SI;
  }
};
